import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GREENING_BRAND } from '../greeningFabActions';
import { useQueryClient } from '@tanstack/react-query';
import greeningApi, { GreeningQuestionItem } from '../../../services/greeningApi';
import { voiceApi } from '../../../services/voiceApi';
import { voiceGuardReply } from './analysisSpeech';
import { speechSentences } from './browserSpeech';
import { GreeningVoiceControls } from './GreeningVoiceControls';
import { GreeningVoiceOverlay } from './GreeningVoiceOverlay';
import { GreeningVoiceVisualizer, VoiceVisualState } from './GreeningVoiceVisualizer';
import { sameSpokenQuestion, VoiceChatReveal } from './voiceChatReveal';
import { GreeningVoicePicker } from './GreeningVoicePicker';
import { useGreeningTts } from './useGreeningTts';
import { ensureStartVoiceProfileId, PADRAO3_BROWSER } from './voiceProfiles';
import { bindVoiceStop, voiceSurfaceOwner } from './voiceSurface';

// A pergunta buscada no histórico (texto cru transcrito) precisa casar com a
// armazenada (SANITIZADA: a sanitização colapsa espaços duplos etc.). Com
// igualdade exata o waitAnswer nunca achava a resposta e a voz não falava.
function normalizeQuestion(text?: string | null): string {
  return (text || '').trim().replace(/\s+/g, ' ').replace(/[.?!,;:\s]+$/g, '').toLowerCase();
}
function sameQuestion(a?: string | null, b?: string | null): boolean {
  return normalizeQuestion(a) === normalizeQuestion(b);
}

// Fala de acolhimento tocada logo após a pergunta, cobrindo o tempo de
// processamento (STT -> consulta à norma -> síntese) para não deixar silêncio.
// Primeira pergunta da sessão: acolhimento completo. Perguntas seguintes: curto.
const VOICE_FILLER_FIRST =
  'Olá, entendi a sua pergunta. Me dê um tempinho para eu buscar a melhor resposta ' +
  'em minha base de dados, analisar melhor o próprio decreto e ver outras correlações ' +
  'a esse assunto em outras bases de dados. Já já eu te retorno com a melhor análise.';
const VOICE_FILLER_FOLLOWUP =
  'Faz sentido esse contexto, me dê um tempinho para analisar e já te retorno.';

interface ContextRef {
  type: string;
  referenceId: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  agentId: string;
  context: ContextRef;
  onReveal: (reveal: VoiceChatReveal | null) => void;
}

const HOLD_LINES = [
  'Olá, entendi a sua pergunta, me dê um tempo para eu cruzar o meu banco de dados com as bases públicas o contexto da sua pergunta, com uma resposta com ação real. Só um pouquinho, já volto com a sua resposta.',
  'Só mais um pouquinho, já já eu respondo.',
  'Sei que estou demorando, mas a resposta será matadora, só mais um pouquinho.',
];
const FOLLOW_UP_HOLD = 'Só um pouquinho, já volto com a sua resposta.';
const HOLD_AT_MS = [0, 30_000, 60_000];
const WAIT_LIMIT_MS = 3 * 60 * 1000;

export function GreeningVoiceModal({ open, onClose, agentId, context, onReveal }: Props) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<VoiceVisualState>('idle');
  const [notice, setNotice] = useState<string | null>(null);
  const [held, setHeld] = useState<'listening' | 'speaking' | null>(null);
  const awaitingAnswerRef = useRef(false);
  const waitSeqRef = useRef(0);
  const answeredOnceRef = useRef(false);
  const holdSpeakingRef = useRef(false);
  const pendingAnswerRef = useRef<{ question: string; answer: string } | null>(null);
  const deliverAnswerRef = useRef<(question: string, answer: string) => void>(() => undefined);
  const stateRef = useRef(state);
  const heldRef = useRef(held);
  stateRef.current = state;
  heldRef.current = held;
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const sessionRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const dropAudioRef = useRef(false);
  const linesRef = useRef<string[]>([]);
  const holdTextRef = useRef('');
  const modeRef = useRef<'lag' | 'idle'>('idle');
  // Reveal (destaque do texto) em índice ABSOLUTO: ao trocar de voz a fala retoma
  // de onde parou (fila fatiada), então o progress.index volta a 0 — somamos a base
  // (índice onde a retomada começou) para o destaque continuar, não reiniciar.
  const revealBaseRef = useRef(0);
  const revealAbsRef = useRef(0);
  const questionRef = useRef('');
  const firstQuestionRef = useRef(true);
  const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onRevealRef = useRef(onReveal);
  onRevealRef.current = onReveal;

  const releaseMic = useCallback(() => {
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
    setAnalyser(null);
  }, []);

  const publishReveal = useCallback((shown: number, activeIndex = 0, overall = 0) => {
    onRevealRef.current({
      question: questionRef.current,
      lines: linesRef.current,
      shown,
      activeIndex,
      overall,
    });
  }, []);

  const stopReveal = useCallback(() => {
    if (revealTimerRef.current != null) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  }, []);

  const { speak, stop, pause, resume, getCursor, activeDisplayName, fallbackNotice, progress } = useGreeningTts(() => {
    setHeld(null);
    heldRef.current = null;
    if (holdSpeakingRef.current) {
      holdSpeakingRef.current = false;
      stateRef.current = 'processing';
      const pending = pendingAnswerRef.current;
      if (pending) {
        pendingAnswerRef.current = null;
        deliverAnswerRef.current(pending.question, pending.answer);
        return;
      }
      if (awaitingAnswerRef.current) {
        setState('processing');
        return;
      }
    }
    if (awaitingAnswerRef.current) {
      stateRef.current = 'processing';
      setState('processing');
      return;
    }
    modeRef.current = 'idle';
    setState('idle');
    if (linesRef.current.length) publishReveal(linesRef.current.length, -1, 1);
  });

  useEffect(() => bindVoiceStop('talk', () => stop()), [stop]);

  const refreshHistory = useCallback(() => {
    const legislationId = Number(context.referenceId);
    if (!legislationId) return;
    queryClient.invalidateQueries({ queryKey: ['greening-history', legislationId] });
  }, [context.referenceId, queryClient]);

  const beginAnswer = useCallback((question: string, answer: string) => {
    awaitingAnswerRef.current = false;
    answeredOnceRef.current = true;
    stopReveal();
    stop();
    questionRef.current = question;
    const sentences = speechSentences(answer);
    linesRef.current = sentences.length ? sentences : [answer.trim()].filter(Boolean);
    holdTextRef.current = '';
    modeRef.current = linesRef.current.length ? 'lag' : 'idle';
    publishReveal(linesRef.current.length, 0, 0);
    if (!linesRef.current.length) {
      setState('idle');
      return;
    }
    setState('speaking');
    speak(
      linesRef.current.map((text) => ({ text, pauseAfterMs: 0 })),
      { sentenceBuffer: true },
    );
  }, [publishReveal, speak, stop, stopReveal]);

  deliverAnswerRef.current = (question, answer) => {
    holdSpeakingRef.current = false;
    awaitingAnswerRef.current = false;
    waitSeqRef.current += 1;
    beginAnswer(question, answer);
  };

  useEffect(() => {
    if (modeRef.current !== 'lag' || !progress || !linesRef.current.length) return;
    const last = Math.max(0, linesRef.current.length - 1);
    const fromSection = /^sent-(\d+)/.exec(progress.section || '');
    const line = fromSection ? Number(fromSection[1]) : progress.index;
    publishReveal(linesRef.current.length, Math.min(line, last), progress.overall);
  }, [progress, publishReveal]);

  const shutdown = useCallback(async () => {
    waitSeqRef.current += 1;
    awaitingAnswerRef.current = false;
    answeredOnceRef.current = false;
    holdSpeakingRef.current = false;
    pendingAnswerRef.current = null;
    stopReveal();
    modeRef.current = 'idle';
    if (voiceSurfaceOwner() !== 'listen') stop();
    releaseMic();
    const id = sessionRef.current;
    sessionRef.current = null;
    if (id) {
      try {
        await voiceApi.close(id);
      } catch {
        /* sessão já encerrada ou voz indisponível */
      }
    }
  }, [releaseMic, stop, stopReveal]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    firstQuestionRef.current = true; // nova sessão: próxima pergunta usa o acolhimento completo
    setNotice(null);
    setHeld(null);
    setState('idle');
    onRevealRef.current(null);
    voiceApi
      .openSession({ agentId, contextType: context.type, referenceId: context.referenceId })
      .then((session) => {
        if (cancelled) {
          voiceApi.close(session.session_id).catch(() => undefined);
          return;
        }
        sessionRef.current = session.session_id;
        generationRef.current = session.generation;
      })
      .catch(() => {
        if (!cancelled) {
          setNotice('Voz indisponível. O chat por texto continua funcionando.');
          setState('error');
        }
      });
    // Pede a permissão de microfone já na abertura (se ainda não concedida), para o
    // usuário poder falar direto. Solta as tracks na hora — aqui só queremos o
    // consentimento; a gravação em si acontece no botão "Falar com a Greening".
    navigator.mediaDevices
      ?.getUserMedia({ audio: true })
      .then((s) => s.getTracks().forEach((t) => t.stop()))
      .catch(() => {
        if (!cancelled) {
          setNotice('Permita o microfone para falar com a Greening. O chat por texto continua disponível.');
        }
      });
    return () => {
      cancelled = true;
      shutdown();
    };
  }, [open, agentId, context.type, context.referenceId, shutdown]);

  const waitAnswer = async (
    question: string,
    generation: number,
    immediate?: string,
    questionId?: number,
  ) => {
    if (immediate) {
      if (generation !== generationRef.current) return;
      refreshHistory();
    }
    const legislationId = Number(context.referenceId);
    const waitId = waitSeqRef.current + 1;
    waitSeqRef.current = waitId;
    awaitingAnswerRef.current = true;
    const started = Date.now();
    const spoken = new Set<number>();
    const holdLines = answeredOnceRef.current ? [FOLLOW_UP_HOLD] : HOLD_LINES;
    const holdAt = answeredOnceRef.current ? [0] : HOLD_AT_MS;
    const stillMine = () => waitSeqRef.current === waitId && generation === generationRef.current;
    const leaveWait = () => {
      if (waitSeqRef.current !== waitId) return;
      awaitingAnswerRef.current = false;
      pendingAnswerRef.current = null;
      stop();
    };
    const playDueHold = () => {
      if (!stillMine() || !awaitingAnswerRef.current) return;
      if (pendingAnswerRef.current && spoken.size > 0) return;
      if (holdSpeakingRef.current || heldRef.current || stateRef.current === 'paused' || stateRef.current === 'speaking') return;
      const elapsed = Date.now() - started;
      const index = holdAt.findIndex((at, i) => !spoken.has(i) && elapsed >= at);
      if (index < 0) return;
      spoken.add(index);
      holdSpeakingRef.current = true;
      holdTextRef.current = holdLines[index];
      stateRef.current = 'speaking';
      setState('speaking');
      speak(holdLines[index]);
    };
    const queueAnswer = (spokenQuestion: string, answer: string) => {
      pendingAnswerRef.current = { question: spokenQuestion, answer };
      refreshHistory();
      if (holdSpeakingRef.current) return;
      if (spoken.size === 0) {
        playDueHold();
        if (holdSpeakingRef.current) return;
      }
      const pending = pendingAnswerRef.current;
      if (!pending) return;
      pendingAnswerRef.current = null;
      deliverAnswerRef.current(pending.question, pending.answer);
    };
    const findRow = (rows: GreeningQuestionItem[]) => {
      if (questionId) {
        const byId = rows.find((item) => item.id === questionId);
        if (byId) return byId;
      }
      return [...rows].reverse().find((item) => sameSpokenQuestion(item.question, question));
    };
    const look = async () => {
      const cached = queryClient.getQueryData<GreeningQuestionItem[]>(['greening-history', legislationId]) || [];
      let fetched: GreeningQuestionItem[] = [];
      try {
        fetched = await greeningApi.getHistory(legislationId);
      } catch {
        fetched = [];
      }
      const byId = new Map<number, GreeningQuestionItem>();
      [...cached, ...fetched].forEach((item) => byId.set(item.id, item));
      const row = findRow(Array.from(byId.values()));
      if (row?.question) {
        questionRef.current = row.question;
        if (!linesRef.current.length) publishReveal(0);
      }
      if (row?.answer?.trim()) return { kind: 'ready' as const, answer: row.answer, question: row.question };
      if (row && (row.status === 'failed' || row.status === 'blocked_by_security')) return { kind: 'failed' as const };
      return { kind: 'wait' as const };
    };
    const giveUp = (kind: 'failed' | 'timeout') => {
      leaveWait();
      setNotice('A resposta ainda está no chat. A voz não bloqueia o texto.');
      setState(kind === 'failed' ? 'error' : 'idle');
    };

    let seen = immediate
      ? { kind: 'ready' as const, answer: immediate, question }
      : await look();
    if (!stillMine()) {
      leaveWait();
      return;
    }
    if (seen.kind === 'ready') {
      queueAnswer(seen.question || question, seen.answer);
      if (pendingAnswerRef.current || !stillMine()) return;
    } else if (seen.kind === 'failed') {
      giveUp('failed');
      return;
    } else {
      playDueHold();
    }
    while (Date.now() - started < WAIT_LIMIT_MS) {
      if (!stillMine()) {
        leaveWait();
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (!stillMine()) {
        leaveWait();
        return;
      }
      seen = await look();
      if (!stillMine()) {
        leaveWait();
        return;
      }
      if (seen.kind === 'ready') {
        queueAnswer(seen.question || question, seen.answer);
        if (pendingAnswerRef.current || !stillMine()) return;
        return;
      }
      if (seen.kind === 'failed') {
        giveUp('failed');
        return;
      }
      playDueHold();
    }
    if (stillMine()) giveUp('timeout');
  };

  const sendAudio = async (blob: Blob, generation: number) => {
    const sessionId = sessionRef.current;
    if (!sessionId || generation !== generationRef.current) return;
    setState('processing');
    try {
      const transcribed = await voiceApi.transcribe(sessionId, blob, generation);
      if (transcribed.generation !== generationRef.current) return;
      questionRef.current = transcribed.text;
      linesRef.current = [];
      publishReveal(0);
      refreshHistory();
      // Preenche o silêncio: fala de acolhimento enquanto consulta a norma (ask)
      // + sintetiza a resposta. A abertura é tratada como "hold": se a resposta
      // chegar no meio dela, fica pendente e só é falada quando a abertura
      // terminar (o onEnd do TTS entrega) — a voz nunca corta a frase de espera.
      // 1ª pergunta da sessão = acolhimento completo; seguintes = frase curta.
      const filler = firstQuestionRef.current ? VOICE_FILLER_FIRST : VOICE_FILLER_FOLLOWUP;
      firstQuestionRef.current = false;
      holdSpeakingRef.current = true;
      holdTextRef.current = filler;
      stateRef.current = 'speaking';
      setState('speaking');
      speak([{ text: filler, pauseAfterMs: 0 }]);
      const asked = await voiceApi.ask(sessionId, transcribed.text, transcribed.generation);
      if (asked.generation !== generationRef.current) return;
      refreshHistory();
      const payload = asked.ask || {};
      if (payload.status === 'failed') {
        refreshHistory();
        setNotice(
          (typeof payload.user_message === 'string' && payload.user_message.trim()) ||
            'Não foi possível enviar a pergunta à GreeningAI. Use o chat por texto.',
        );
        setState('error');
        return;
      }
      const refusal = voiceGuardReply(payload);
      if (refusal) {
        refreshHistory();
        beginAnswer(transcribed.text, refusal);
        return;
      }
      const immediate =
        payload.answer ||
        (payload.status === 'completed' ||
        payload.status === 'rate_limited' ||
        payload.status === 'insufficient_context'
          ? payload.user_message
          : undefined);
      const questionId = typeof payload.question_id === 'number' ? payload.question_id : undefined;
      await waitAnswer(transcribed.text, asked.generation, immediate, questionId);
      refreshHistory();
    } catch (err) {
      if (generation === generationRef.current) {
        refreshHistory();
        const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        const message = typeof detail === 'string' && detail.trim() ? detail : 'Não foi possível transcrever. Use o chat por texto.';
        setNotice(message);
        setState('error');
      }
    }
  };

  const onListen = async () => {
    if (!sessionRef.current) return;
    ensureStartVoiceProfileId();
    setNotice(null);
    setState('requesting_permission');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const node = audioCtx.createAnalyser();
      node.fftSize = 512;
      source.connect(node);
      setAnalyser(node);
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      const generation = generationRef.current;
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        releaseMic();
        if (dropAudioRef.current) {
          dropAudioRef.current = false;
          return;
        }
        sendAudio(blob, generation);
      };
      recorderRef.current = recorder;
      recorder.start();
      setState('listening');
    } catch {
      setNotice('Microfone não autorizado. A conversa por texto permanece disponível.');
      setState('error');
      releaseMic();
    }
  };

  const onStopListen = () => {
    setHeld(null);
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    else setState('idle');
  };

  const onToggleHold = () => {
    if (state === 'paused') {
      const from = held;
      setHeld(null);
      if (from === 'listening') {
        const recorder = recorderRef.current;
        if (recorder && recorder.state === 'paused') recorder.resume();
        setState('listening');
        return;
      }
      resume();
      setState('speaking');
      return;
    }
    if (state === 'listening' || state === 'speech_detected') {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state !== 'recording' || typeof recorder.pause !== 'function') return;
      recorder.pause();
      setHeld('listening');
      setState('paused');
      return;
    }
    if (state === 'speaking') {
      pause();
      setHeld('speaking');
      setState('paused');
    }
  };

  return (
    <GreeningVoiceOverlay
      open={open}
      framed
      title="Conversar com a GreeningAI"
      subtitle="O microfone só liga neste painel. A norma é a do chat."
      onClose={() => {
        stopReveal();
        stop();
        onClose();
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
        <Typography
          variant="caption"
          align="center"
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: '16px',
            color: '#fff',
            backgroundImage: `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`,
          }}
        >
          A pergunta e a resposta aparecem no chat.
        </Typography>
      </Box>
      <GreeningVoicePicker
        compact
        vivid
        excludeIds={[PADRAO3_BROWSER.id]}
        onChange={(profileId) => {
          if (state !== 'speaking' && state !== 'paused') return;
          if (held === 'listening') return;
          const cursor = getCursor();
          if (holdSpeakingRef.current && holdTextRef.current) {
            setHeld(null);
            setState('speaking');
            speak(holdTextRef.current, { profileId, resume: cursor });
            return;
          }
          if (modeRef.current !== 'lag' || !linesRef.current.length) return;
          setHeld(null);
          setState('speaking');
          speak(
            linesRef.current.map((text) => ({ text, pauseAfterMs: 0 })),
            { sentenceBuffer: true, profileId, resume: cursor },
          );
        }}
      />
      {fallbackNotice ? (
        <Typography variant="caption" color="warning.main" display="block" sx={{ textAlign: 'center', mt: 0.5, mb: 0.5 }}>
          {fallbackNotice}
        </Typography>
      ) : null}
      <GreeningVoiceVisualizer state={state} analyser={analyser} />
      {state === 'speaking' && activeDisplayName ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
          <Typography
            variant="caption"
            align="center"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: '16px',
              color: '#fff',
              backgroundImage: `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`,
            }}
          >
            Lendo com {activeDisplayName}
          </Typography>
        </Box>
      ) : null}
      {notice && (
        <Alert
          severity={state === 'error' ? 'warning' : 'info'}
          sx={{
            my: 1,
            color: 'text.primary',
            bgcolor: 'transparent',
            backgroundImage: 'none',
            border: `1px solid ${alpha(GREENING_BRAND.green, 0.35)}`,
            boxShadow: 'none',
            '& .MuiAlert-icon': { color: GREENING_BRAND.green },
            '&.MuiAlert-standardWarning, &.MuiAlert-standardInfo': {
              bgcolor: 'transparent',
              color: 'text.primary',
            },
          }}
        >
          {notice}
        </Alert>
      )}
      <GreeningVoiceControls
        state={state}
        held={held}
        onListen={onListen}
        onStopListen={onStopListen}
        onToggleHold={onToggleHold}
      />
    </GreeningVoiceOverlay>
  );
}
